import React, { useState, useEffect } from 'react';
import { Square, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '@/utils/api';

// Global timer state management using CustomEvent
const TIMER_EVENTS = {
  TIMER_STARTED: 'timer-started',
  TIMER_STOPPED: 'timer-stopped',
  TIMER_UPDATED: 'timer-updated',
  TIMER_PAUSED: 'timer-paused',
  TIMER_RESUMED: 'timer-resumed'
};

// Global timer bar component - continuous bar under header
const GlobalTimerBar = () => {
  const { t } = useTranslation();
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerDuration, setTimerDuration] = useState(0);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');

  const fetchActiveTimer = async () => {
    try {
      const response = await api.get('/time-entries/active');
      setActiveTimer(response.data);
      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent(TIMER_EVENTS.TIMER_UPDATED, { 
        detail: response.data 
      }));
    } catch (error) {
      // Silent fail - no active timer is normal
      setActiveTimer(null);
      window.dispatchEvent(new CustomEvent(TIMER_EVENTS.TIMER_UPDATED, { 
        detail: null 
      }));
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data.filter(p => p.status === "active"));
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  useEffect(() => {
    // Initial data fetch
    const initializeData = async () => {
      await fetchActiveTimer();
      await fetchProjects();
    };
    
    initializeData();
  }, []);

  useEffect(() => {
    // Listen for timer events from other components
    const handleTimerStarted = (event) => {
      setActiveTimer(event.detail);
    };

    const handleTimerStopped = () => {
      setActiveTimer(null);
      setTimerDuration(0);
    };

    const handleTimerUpdated = (event) => {
      setActiveTimer(event.detail);
    };

    const handleTimerPaused = (event) => {
      setActiveTimer(event.detail);
    };

    const handleTimerResumed = (event) => {
      setActiveTimer(event.detail);
    };

    window.addEventListener(TIMER_EVENTS.TIMER_STARTED, handleTimerStarted);
    window.addEventListener(TIMER_EVENTS.TIMER_STOPPED, handleTimerStopped);
    window.addEventListener(TIMER_EVENTS.TIMER_UPDATED, handleTimerUpdated);
    window.addEventListener(TIMER_EVENTS.TIMER_PAUSED, handleTimerPaused);
    window.addEventListener(TIMER_EVENTS.TIMER_RESUMED, handleTimerResumed);

    return () => {
      window.removeEventListener(TIMER_EVENTS.TIMER_STARTED, handleTimerStarted);
      window.removeEventListener(TIMER_EVENTS.TIMER_STOPPED, handleTimerStopped);
      window.removeEventListener(TIMER_EVENTS.TIMER_UPDATED, handleTimerUpdated);
      window.removeEventListener(TIMER_EVENTS.TIMER_PAUSED, handleTimerPaused);
      window.removeEventListener(TIMER_EVENTS.TIMER_RESUMED, handleTimerResumed);
    };
  }, []);

  useEffect(() => {
    let interval;
    if (activeTimer && !activeTimer.is_paused) {
      interval = setInterval(() => {
        const start = new Date(activeTimer.start_time);
        const now = new Date();
        const totalElapsed = Math.floor((now - start) / 1000);
        const pauseDuration = Math.round((activeTimer.total_pause_duration || 0) * 60); // Convert minutes to seconds and round
        const netDuration = totalElapsed - pauseDuration;
        setTimerDuration(Math.max(0, Math.floor(netDuration))); // Ensure integer seconds
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  const startTimer = async (projectId) => {
    if (!projectId) return;
    
    try {
      const response = await api.post(
        `/time-entries/start?project_id=${projectId}&description=${encodeURIComponent('')}`
      );
      setActiveTimer(response.data);
      setSelectedProjectId('');
      toast.success(t('timeTracking.timerStarted', 'Timer gestartet'));
      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent(TIMER_EVENTS.TIMER_STARTED, { 
        detail: response.data 
      }));
    } catch (error) {
      if (error.isGuestReadonly) return;
      toast.error(error.response?.data?.detail || t('timeTracking.startTimerError', 'Fehler beim Starten des Timers'));
    }
  };

  const stopTimer = async () => {
    try {
      await api.post('/time-entries/stop');
      setActiveTimer(null);
      setTimerDuration(0);
      toast.success(t('timeTracking.timerStopped', 'Timer gestoppt'));
      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent(TIMER_EVENTS.TIMER_STOPPED));
    } catch (error) {
      if (error.isGuestReadonly) return;
      toast.error(t('timeTracking.stopTimerError', 'Fehler beim Stoppen des Timers'));
    }
  };

  const pauseTimer = async () => {
    if (!activeTimer) return;
    
    try {
      const response = await api.post(`/time-entries/${activeTimer.id}/pause`);
      setActiveTimer(response.data);
      toast.success(t('timeTracking.timerPaused', 'Timer pausiert'));
      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent(TIMER_EVENTS.TIMER_PAUSED, { 
        detail: response.data 
      }));
    } catch (error) {
      if (error.isGuestReadonly) return;
      toast.error(t('timeTracking.pauseTimerError', 'Fehler beim Pausieren des Timers'));
    }
  };

  const resumeTimer = async () => {
    if (!activeTimer) return;
    
    try {
      const response = await api.post(`/time-entries/${activeTimer.id}/resume`);
      setActiveTimer(response.data);
      toast.success(t('timeTracking.timerResumed', 'Timer fortgesetzt'));
      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent(TIMER_EVENTS.TIMER_RESUMED, { 
        detail: response.data 
      }));
    } catch (error) {
      if (error.isGuestReadonly) return;
      toast.error(t('timeTracking.resumeTimerError', 'Fehler beim Fortsetzen des Timers'));
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Always render the bar, but with different states
  if (!activeTimer) {
    // Inactive state - transparent but visible with project selection
    return (
      <div className="w-full bg-gray-50 border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-2">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-y-2">
          <div className="flex items-center space-x-4">
            <div className="h-2 w-2 rounded-full bg-gray-300"></div>
            <div className="font-mono text-lg font-semibold text-gray-500">
              00:00:00
            </div>
            <span className="text-sm text-gray-500">
              {t('timeTracking.noActiveTimer', 'Kein aktiver Timer')}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1 bg-white max-w-[150px] truncate"
            >
              <option value="">{t('timeTracking.selectProject', 'Projekt auswählen...')}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="outline"
              onClick={() => startTimer(selectedProjectId)}
              disabled={!selectedProjectId}
              className="h-8 px-3 text-emerald-600 border-emerald-600 hover:bg-emerald-50"
            >
              <Play className="h-4 w-4 mr-1" />
              {t('timeTracking.start', 'Start')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Active or paused state
  const isActive = !activeTimer.is_paused;
  const isPaused = activeTimer.is_paused;

  return (
    <div className={`w-full text-white px-4 sm:px-6 lg:px-8 py-2 ${
      isPaused ? 'bg-yellow-500' : 'bg-emerald-600'
    }`}>
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-y-2">
        <div className="flex items-center space-x-4 min-w-0">
          <div className={`h-2 w-2 rounded-full ${
            isPaused ? 'bg-yellow-200' : 'bg-red-400 animate-pulse'
          }`}></div>
          <div className="font-mono text-lg font-semibold">
            {formatDuration(timerDuration)}
          </div>
          <span className="text-sm font-medium truncate">
            {activeTimer.project_name}
          </span>
          {isPaused && (
            <span className="text-sm">
              {t('timeTracking.paused', 'Pausiert')}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={isPaused ? resumeTimer : pauseTimer}
            className={`h-8 w-8 p-0 text-white hover:bg-opacity-20 hover:bg-white ${
              isPaused ? 'hover:text-yellow-100' : 'hover:text-emerald-100'
            }`}
            title={isPaused 
              ? t('timeTracking.resumeTimer', 'Timer fortsetzen')
              : t('timeTracking.pauseTimer', 'Timer pausieren')
            }
          >
            {isPaused ? (
              <Play className="h-4 w-4" fill="currentColor" />
            ) : (
              <Pause className="h-4 w-4" fill="currentColor" />
            )}
          </Button>
          
          <Button
            size="sm"
            variant="ghost"
            onClick={stopTimer}
            className={`h-8 w-8 p-0 text-white hover:bg-opacity-20 hover:bg-white ${
              isPaused ? 'hover:text-yellow-100' : 'hover:text-emerald-100'
            }`}
            title={t('timeTracking.stopTimer', 'Timer stoppen')}
          >
            <Square className="h-4 w-4" fill="currentColor" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Legacy TimerWidget component - now just returns null since we use GlobalTimerBar
const TimerWidget = () => {
  return null;
};

export default TimerWidget;
export { GlobalTimerBar, TIMER_EVENTS };